import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ErrorPage from "../Components/ErrorPage";
import AccountTransactionAdd from "./AccountTransactionAdd";
import AccountTransactionView from "./AccountTransactionView";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

import { formatJalaliDate, formatJalaliDateTime } from "../utils/dateFormat";
const typeCellSx = (value) => {
  if (value === "debit") return { color: "success.main", fontWeight: 600 };
  if (value === "credit") return { color: "error.main", fontWeight: 600 };
  return {};
};

const getDisplayType = (row) => row?.transaction_type;

const formatSignedAmount = (row) => {
  const type = getDisplayType(row);
  const rawAmount = row?.amount ?? "0";
  const numeric = Number(rawAmount);
  const absAmount = Number.isFinite(numeric)
    ? Math.abs(numeric).toString()
    : String(rawAmount).replace(/^-/, "");

  if (type === "debit") return `+${absAmount}`;
  if (type === "credit") return `-${absAmount}`;
  return absAmount;
};

const getGroupKey = (row) => {
  const baseDesc = ((row?.description || "").split("|")[0] || "").trim().toLowerCase();
  const txDate = row?.transaction_date || "";
  return `${baseDesc}__${txDate}`;
};

const getFallbackPairKey = (row) => {
  const txDate = row?.transaction_date || "";
  const amount = String(row?.amount ?? "");
  const currency = String(row?.transaction_currency_title ?? "");
  return `${txDate}__${amount}__${currency}`;
};

const getBaseDescription = (row) =>
  ((row?.description || "").split("|")[0] || "").trim().toLowerCase();

const AccountTransactionIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [openAccounting, setOpenAccounting] = useState(false);
  const [accountingRows, setAccountingRows] = useState([]);
  const [accountingTitle, setAccountingTitle] = useState("");

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["account-transactions", searchTerm, page, rowsPerPage],
    `shop/account-transactions/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/account-transactions", token);

  const rows = data?.results || data || [];

  const groupedByKey = useMemo(() => {
    const grouped = {};
    rows.forEach((row) => {
      const key = getGroupKey(row);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }, [rows]);

  const groupedByFallback = useMemo(() => {
    const grouped = {};
    rows.forEach((row) => {
      const key = getFallbackPairKey(row);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }, [rows]);

  const groupHasBothSides = (groupRows) => {
    const hasDebit = groupRows.some((r) => getDisplayType(r) === "debit");
    const hasCredit = groupRows.some((r) => getDisplayType(r) === "credit");
    return hasDebit && hasCredit;
  };

  const totalCount = data?.count ?? rows.length;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (_event, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const renderRows = (tableRows) =>
    tableRows.map((row, i) => {
      const groupKey = getGroupKey(row);
      let groupRows = groupedByKey[groupKey] || [];
      if (groupRows.length < 2) {
        const fallbackRows = groupedByFallback[getFallbackPairKey(row)] || [];
        const hasBothFallback =
          fallbackRows.some((r) => getDisplayType(r) === "debit") &&
          fallbackRows.some((r) => getDisplayType(r) === "credit");
        if (hasBothFallback) {
          groupRows = fallbackRows;
        }
      }
      if (groupRows.length < 2) {
        const baseDesc = getBaseDescription(row);
        const txDate = row?.transaction_date || "";
        const byDescDate = rows.filter(
          (r) =>
            getBaseDescription(r) === baseDesc &&
            String(r?.transaction_date || "") === String(txDate)
        );
        if (byDescDate.length >= 2) {
          groupRows = byDescDate;
        }
      }
      const canViewAccounting = groupHasBothSides(groupRows);
      const isPrimaryRowInPair =
        !canViewAccounting ||
        getDisplayType(row) === "debit" ||
        (groupRows[0] && groupRows[0].id === row.id);
      if (!isPrimaryRowInPair) return null;

      const currentType = getDisplayType(row);
      const oppositeType = currentType === "debit" ? "credit" : "debit";
      const orderedJournalRows = groupRows.filter(
        (entry) => getDisplayType(entry) === oppositeType
      );
      const showAccountingButton = canViewAccounting;

      return (
        <React.Fragment key={row.id}>
          <TableRow hover>
            <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
            <TableCell>{row.account_name || "-"}</TableCell>
            <TableCell sx={typeCellSx(getDisplayType(row))}>
              {getDisplayType(row) === "credit"
                ? t("accountTransaction.credit")
                : t("accountTransaction.debit")}
            </TableCell>
            <TableCell sx={typeCellSx(getDisplayType(row))}>{formatSignedAmount(row)}</TableCell>
            <TableCell>{row.transaction_currency_title || "-"}</TableCell>
            <TableCell>{row.exchange_rate}</TableCell>
            <TableCell>{row.converted_amount}</TableCell>
            <TableCell>{row.account_currency_title || "-"}</TableCell>
            <TableCell>{formatJalaliDate(row.transaction_date)}</TableCell>
            <TableCell>{row.description || "-"}</TableCell>
            <TableCell>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, py: 0.25 }}>
                  {showAccountingButton && (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ minHeight: 38, px: 2, fontWeight: 700, textTransform: "uppercase", whiteSpace: "nowrap" }}
                      onClick={() => {
                        setAccountingRows(groupRows);
                        setAccountingTitle((row?.description || "").split("|")[0] || "Journal Entry");
                        setOpenAccounting(true);
                      }}
                    >
                     VIEW ACCOUNTING
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ minHeight: 38, px: 2 }}
                    onClick={() => {
                      setViewData(row);
                      setOpenView(true);
                    }}
                  >
                    {t("accountTransaction.view")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    sx={{ minHeight: 38, px: 2 }}
                    onClick={() => handleDelete(row.id)}
                  >
                    {t("accountTransaction.delete")}
                  </Button>
              </Box>
            </TableCell>
          </TableRow>
        </React.Fragment>
      );
    });

  if (isError) return <ErrorPage message={t("accountTransaction.failedToLoad")} />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">{t("accountTransaction.listTitle")}</Typography>
        <Button variant="contained" color="primary" onClick={() => setOpenAdd(true)}>
          {t("accountTransaction.addNew")}
        </Button>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("accountTransaction.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("accountTransaction.searchPlaceholder")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="account transaction table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.index")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.account")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.type")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.amount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.transactionCurrency")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.exchangeRate")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.convertedAmount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.accountCurrency")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.transactionDate")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.description")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("accountTransaction.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from(new Array(11)).map((__, j) => (
                      <TableCell key={`skeleton-${i}-${j}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : renderRows(rows)}
          </TableBody>
        </Table>
      </TableContainer>


      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("accountTransaction.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage) || 1}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      <ConfirmDialog />

      {openAdd && (
        <AccountTransactionAdd open={openAdd} onClose={() => setOpenAdd(false)} token={token} />
      )}

      {openView && (
        <AccountTransactionView open={openView} onClose={() => setOpenView(false)} data={viewData} />
      )}

      <Dialog
        open={openAccounting}
        onClose={() => setOpenAccounting(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{accountingTitle}</DialogTitle>
        <DialogContent>
          {(() => {
            const debitEntries = accountingRows.filter((e) => getDisplayType(e) === "debit");
            const creditEntries = accountingRows.filter((e) => getDisplayType(e) === "credit");

            const renderJournalTable = (entries, title) => (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {title}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Account</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Currency</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={`modal-jrnl-${entry.id}`}>
                          <TableCell>{entry.account_name || "-"}</TableCell>
                          <TableCell sx={typeCellSx(getDisplayType(entry))}>
                            {getDisplayType(entry) === "credit"
                              ? t("accountTransaction.credit")
                              : t("accountTransaction.debit")}
                          </TableCell>
                          <TableCell sx={typeCellSx(getDisplayType(entry))}>{formatSignedAmount(entry)}</TableCell>
                          <TableCell>{entry.transaction_currency_title || "-"}</TableCell>
                          <TableCell>{formatJalaliDate(entry.transaction_date)}</TableCell>
                          <TableCell>{entry.description || "-"}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityOutlinedIcon />}
                              onClick={() => {
                                setViewData(entry);
                                setOpenView(true);
                              }}
                            >
                              {t("accountTransaction.view")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );

            return (
              <>
                {renderJournalTable(debitEntries, "Debit Entries")}
                {renderJournalTable(creditEntries, "Credit Entries")}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AccountTransactionIndex;
